import { useEffect, useState } from 'react';

/**
 * Copy-to-clipboard for a share link, with a confirmation that does not outlive the dialog.
 *
 * Its own hook because BOTH share dialogs need it and neither should own a copy of the rule: a denied
 * clipboard is not worth an error of its own (the field is selectable, so the link is still takeable
 * by hand), and the "Copied" state has to clear itself or it reads as a permanent label.
 */
export function useShareLinkCopy(): { copied: boolean; copyLink: (link: string) => Promise<void> } {
  const [copied, setCopied] = useState(false);

  // Transient, and it must not fire into an unmounted dialog.
  useEffect(() => {
    if (!copied) return undefined;
    const timer = window.setTimeout(() => setCopied(false), 2000);
    return () => window.clearTimeout(timer);
  }, [copied]);

  const copyLink = async (link: string) => {
    if (!link) return;
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
    } catch {
      // Focus/permission can deny the clipboard. The field is select-on-focus, so the link is still
      // takeable — a dialog about it would be louder than the problem.
      setCopied(false);
    }
  };

  return { copied, copyLink };
}
